import { ArrowRight, Loader2, Sparkles } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

import {
  useCreateMilestone,
  useOllamaModels,
  useSuggestMilestones,
  useSuggestWeights,
  useUpdateMilestone,
  type MilestoneSuggestion,
  type Objective,
} from "../api";
import { weightApplyOrder } from "../milestoneOrder";

interface Props {
  objective: Objective;
  onClose: () => void;
}

interface EditableSuggestion extends MilestoneSuggestion {
  accepted: boolean;
}

interface EditableWeight {
  milestone_id: string;
  title: string;
  current: number;
  proposed: number;
  rationale: string | null;
  accepted: boolean;
}

type Mode = "create" | "weights";

export function MilestoneSuggestDialog({ objective, onClose }: Props) {
  const models = useOllamaModels();
  const suggest = useSuggestMilestones();
  const suggestWeights = useSuggestWeights();
  const create = useCreateMilestone();
  const update = useUpdateMilestone();

  const hasMilestones = objective.milestones.length > 0;
  const [mode, setMode] = useState<Mode>("create");
  const [model, setModel] = useState<string>("");
  const [countHint, setCountHint] = useState(5);
  const [items, setItems] = useState<EditableSuggestion[] | null>(null);
  const [weightItems, setWeightItems] = useState<EditableWeight[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);

  // Default the model select to the configured/effective default once loaded.
  const effectiveModel = model || models.data?.default_model || "";
  const pending = suggest.isPending || suggestWeights.isPending;

  function switchMode(next: Mode) {
    setMode(next);
    setError(null);
  }

  function runSuggest() {
    setError(null);
    if (mode === "weights") {
      suggestWeights.mutate(
        { objectiveId: objective.id, model: effectiveModel || undefined },
        {
          onSuccess: (res) => {
            if (res.suggestions.length === 0) {
              setError(
                res.raw_text
                  ? `The model didn't return usable weights. Raw: ${res.raw_text.slice(0, 200)}`
                  : "The model returned no weights. Try a different model or add more plan detail.",
              );
              setWeightItems([]);
              return;
            }
            const byId = new Map(objective.milestones.map((m) => [m.id, m]));
            setWeightItems(
              res.suggestions.flatMap((s) => {
                const m = byId.get(s.milestone_id);
                if (!m) return [];
                return {
                  milestone_id: s.milestone_id,
                  title: m.title,
                  current: m.weight,
                  proposed: s.weight,
                  rationale: s.rationale,
                  accepted: true,
                };
              }),
            );
          },
          onError: (e) => {
            setError(e instanceof ApiError ? e.message : String(e));
          },
        },
      );
      return;
    }
    suggest.mutate(
      { objectiveId: objective.id, model: effectiveModel || undefined, count_hint: countHint },
      {
        onSuccess: (res) => {
          if (res.suggestions.length === 0) {
            setError(
              res.raw_text
                ? `The model didn't return usable milestones. Raw: ${res.raw_text.slice(0, 200)}`
                : "The model returned no milestones. Try a different model or add more plan detail.",
            );
            setItems([]);
            return;
          }
          setItems(res.suggestions.map((s) => ({ ...s, accepted: true })));
        },
        onError: (e) => {
          setError(e instanceof ApiError ? e.message : String(e));
        },
      },
    );
  }

  async function addAccepted() {
    if (!items) return;
    setApplying(true);
    try {
      for (const item of items.filter((i) => i.accepted)) {
        await create.mutateAsync({
          objectiveId: objective.id,
          payload: {
            title: item.title,
            description: item.description ?? undefined,
            weight: item.weight,
            due_date: item.due_date ?? undefined,
          },
        });
      }
      onClose();
    } finally {
      setApplying(false);
    }
  }

  async function applyWeights() {
    if (!weightItems) return;
    setApplying(true);
    try {
      const changes = weightApplyOrder(
        weightItems
          .filter((w) => w.accepted)
          .map((w) => ({ id: w.milestone_id, current: w.current, proposed: w.proposed })),
      );
      for (const change of changes) {
        await update.mutateAsync({ id: change.id, payload: { weight: change.proposed } });
      }
      onClose();
    } finally {
      setApplying(false);
    }
  }

  const notConfigured = models.data && !models.data.configured;
  const acceptedCount = items?.filter((i) => i.accepted).length ?? 0;
  const acceptedWeightCount =
    weightItems?.filter((w) => w.accepted && w.proposed !== w.current).length ?? 0;

  // What the objective's weight total would be after applying the accepted
  // proposals (non-accepted / unreturned milestones keep their current weight).
  const proposedById = new Map(
    (weightItems ?? []).filter((w) => w.accepted).map((w) => [w.milestone_id, w.proposed]),
  );
  const resultingTotal = objective.milestones.reduce(
    (sum, m) => sum + (proposedById.get(m.id) ?? m.weight),
    0,
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-brand" /> AI milestones
          </DialogTitle>
          <DialogDescription>
            {mode === "weights"
              ? `Uses the local AI to read “${objective.title}”'s success definition and plan, then propose how much each existing milestone should weigh. Review and edit before applying.`
              : `Uses the local AI to break “${objective.title}” into milestones from its success definition and plan. Review and edit before adding.`}
          </DialogDescription>
        </DialogHeader>

        {notConfigured ? (
          <p className="text-sm text-destructive">
            No local AI (Ollama) is reachable. Set <code>OLLAMA_HOST</code> on the backend and
            ensure Ollama is running.
          </p>
        ) : (
          <>
            <div className="flex w-fit items-center rounded-lg border border-border bg-muted/40 p-0.5 text-xs font-semibold">
              <button
                type="button"
                onClick={() => switchMode("create")}
                className={cn(
                  "rounded-md px-2.5 py-1",
                  mode === "create"
                    ? "bg-background shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                Suggest new milestones
              </button>
              <button
                type="button"
                onClick={() => switchMode("weights")}
                disabled={!hasMilestones}
                title={hasMilestones ? undefined : "Add milestones first"}
                className={cn(
                  "rounded-md px-2.5 py-1",
                  mode === "weights"
                    ? "bg-background shadow-sm"
                    : "text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50",
                )}
              >
                Set weights for existing
              </button>
            </div>

            <div className="flex flex-wrap items-end gap-3">
              <div className="flex flex-col gap-1">
                <Label>Model</Label>
                <Select
                  value={effectiveModel}
                  onValueChange={setModel}
                  disabled={models.isLoading}
                >
                  <SelectTrigger className="h-9 min-w-[200px] rounded-xl">
                    <SelectValue placeholder="Pick a model" />
                  </SelectTrigger>
                  <SelectContent>
                    {models.data?.models.map((m) => (
                      <SelectItem key={m.name} value={m.name}>
                        {m.name}
                        {m.is_default ? " (default)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {mode === "create" && (
                <div className="flex flex-col gap-1">
                  <Label htmlFor="ai-count"># of milestones</Label>
                  <Input
                    id="ai-count"
                    type="number"
                    min={1}
                    max={12}
                    value={countHint}
                    onChange={(e) => setCountHint(Number(e.target.value))}
                    className="w-24"
                  />
                </div>
              )}
              <Button type="button" onClick={runSuggest} disabled={pending}>
                {pending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Thinking…
                  </>
                ) : (
                  "Generate"
                )}
              </Button>
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            {mode === "create" && items && items.length > 0 && (
              <div className="flex max-h-[320px] flex-col gap-2 overflow-auto">
                {items.map((item, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-2 rounded-md border border-border p-2"
                  >
                    <Checkbox
                      checked={item.accepted}
                      onCheckedChange={(checked) =>
                        setItems((prev) =>
                          prev!.map((p, j) =>
                            j === i ? { ...p, accepted: checked === true } : p,
                          ),
                        )
                      }
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <input
                        value={item.title}
                        onChange={(e) =>
                          setItems((prev) =>
                            prev!.map((p, j) => (j === i ? { ...p, title: e.target.value } : p)),
                          )
                        }
                        className="w-full bg-transparent text-sm font-medium focus:outline-none"
                      />
                      {item.description && (
                        <p className="text-xs text-muted-foreground">{item.description}</p>
                      )}
                    </div>
                    <input
                      type="number"
                      value={item.weight}
                      min={0}
                      max={100}
                      onChange={(e) =>
                        setItems((prev) =>
                          prev!.map((p, j) =>
                            j === i ? { ...p, weight: Number(e.target.value) } : p,
                          ),
                        )
                      }
                      className="w-16 rounded border border-input bg-background px-1.5 py-0.5 text-right text-xs"
                    />
                  </div>
                ))}
              </div>
            )}

            {mode === "weights" && weightItems && weightItems.length > 0 && (
              <>
                <div className="flex max-h-[320px] flex-col gap-2 overflow-auto">
                  {weightItems.map((item, i) => (
                    <div
                      key={item.milestone_id}
                      className="flex items-start gap-2 rounded-md border border-border p-2"
                    >
                      <Checkbox
                        checked={item.accepted}
                        onCheckedChange={(checked) =>
                          setWeightItems((prev) =>
                            prev!.map((p, j) =>
                              j === i ? { ...p, accepted: checked === true } : p,
                            ),
                          )
                        }
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium">{item.title}</p>
                        {item.rationale && (
                          <p className="text-xs text-muted-foreground">{item.rationale}</p>
                        )}
                      </div>
                      <span className="mt-1 flex items-center gap-1 whitespace-nowrap text-xs text-muted-foreground">
                        {item.current.toFixed(0)}%
                        <ArrowRight className="h-3 w-3" />
                      </span>
                      <input
                        type="number"
                        value={item.proposed}
                        min={0}
                        max={100}
                        onChange={(e) =>
                          setWeightItems((prev) =>
                            prev!.map((p, j) =>
                              j === i ? { ...p, proposed: Number(e.target.value) } : p,
                            ),
                          )
                        }
                        className="w-16 rounded border border-input bg-background px-1.5 py-0.5 text-right text-xs"
                        title="Proposed weight %"
                      />
                    </div>
                  ))}
                </div>
                <p
                  className={cn(
                    "text-xs",
                    Math.abs(resultingTotal - 100) > 0.01
                      ? "text-warning"
                      : "text-muted-foreground",
                  )}
                >
                  Resulting total: {resultingTotal.toFixed(0)}%
                  {Math.abs(resultingTotal - 100) > 0.01 ? " — doesn't sum to 100" : ""}
                </p>
              </>
            )}

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              {mode === "create" && items && items.length > 0 && (
                <Button type="button" onClick={addAccepted} disabled={applying || acceptedCount === 0}>
                  {applying
                    ? "Adding…"
                    : `Add ${acceptedCount} milestone${acceptedCount === 1 ? "" : "s"}`}
                </Button>
              )}
              {mode === "weights" && weightItems && weightItems.length > 0 && (
                <Button
                  type="button"
                  onClick={applyWeights}
                  disabled={applying || acceptedWeightCount === 0 || resultingTotal > 100.005}
                  title={
                    resultingTotal > 100.005
                      ? "Weights can't exceed 100 — lower a proposal first"
                      : undefined
                  }
                >
                  {applying
                    ? "Applying…"
                    : `Apply ${acceptedWeightCount} weight${acceptedWeightCount === 1 ? "" : "s"}`}
                </Button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
